// Extraído de PeticionDetail.jsx (refactor mecánico, sin cambios funcionales)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import RequisitosForm, { RequisitosChips } from "@/components/espacios/RequisitosForm";
import { aFormRequisitos, aPayloadRequisitos, hayRequisitos, requisitosIguales, REQ_VACIO } from "@/lib/requisitosMontaje";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import EspacioCombobox from "@/components/EspacioCombobox";
import TiposLineaMulti from "@/components/TiposLineaMulti";
import { ImagenUploadControl } from "@/components/ProyectoThumb";
import { ESTADOS_LINEA_MANUALES } from "@/lib/peticionWorkflow";

export function NuevaLineaDialog({ open, setOpen, peticionId, espacios, users, onCreated, editing, onClose, clienteId, fechaObjetivoDefault }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    tipo: "solo_montaje", tipos: [], espacio_id: "", zona_id: "", estado: "pendiente",
    descripcion: "", horas_estimadas: "",
    lleva_retirada: false, fecha_retirada: "",
    fecha_programada: "",  // fecha de montaje por línea (opcional · null = «Sin fecha» en calendario)
    coste_montaje: 0, precio_montaje: 0,
    requiere_validacion: false,
    imagen_url: "",
  });
  const [saving, setSaving] = useState(false);
  const espacioSel = espacios.find((e) => e.id === form.espacio_id);
  const zonasDisponibles = espacioSel?.zonas || [];
  // Requisitos de montaje: snapshot editable de la línea (copiado de la zona; nunca referencia viva)
  const [req, setReq] = useState(null);                // form state (aFormRequisitos) o null
  const [reqOrigen, setReqOrigen] = useState(null);    // {zona_id, zona_nombre, modificado}
  const [reqAbierto, setReqAbierto] = useState(false);
  const [reqConfirm, setReqConfirm] = useState(null);  // {zona, nuevos} pendiente de confirmar sustitución
  const zonaSel = zonasDisponibles.find((z) => z.id === form.zona_id);
  const reqPersonalizado = req && hayRequisitos(req) && (!reqOrigen || reqOrigen.modificado || !requisitosIguales(req, aFormRequisitos(zonaSel?.requisitos_montaje)));
  const cargarDeZona = (zona) => {
    const nuevos = zona?.requisitos_montaje && hayRequisitos(zona.requisitos_montaje) ? aFormRequisitos(zona.requisitos_montaje) : null;
    setReq(nuevos); setReqOrigen(nuevos ? { zona_id: zona.id, zona_nombre: zona.nombre, espacio_id: form.espacio_id, modificado: false } : null);
    if (nuevos) setReqAbierto(true);
  };
  const onZonaChange = (zid) => {
    const zona = zonasDisponibles.find((z) => z.id === zid);
    setForm((f) => ({ ...f, zona_id: zid }));
    if (req && hayRequisitos(req) && reqPersonalizado) { setReqConfirm({ zona }); return; }   // no borrar personalizaciones sin confirmar
    cargarDeZona(zona);
  };

  // Si entra en modo edición → cargar valores
  useEffect(() => {
    if (editing) {
      setForm({
        tipo: editing.tipo || "solo_montaje",
        tipos: Array.isArray(editing.tipos) && editing.tipos.length > 0
          ? editing.tipos
          : (editing.tipo === "solo_montaje" ? ["montaje"]
              : editing.tipo === "produccion_montaje" ? ["produccion", "montaje"]
              : editing.tipo === "diseno_produccion_montaje" ? ["diseno", "produccion", "montaje"]
              : editing.tipo ? [editing.tipo] : []),
        espacio_id: editing.espacio_id || "",
        zona_id: editing.zona_id || "",
        estado: editing.estado || "pendiente",
        descripcion: editing.descripcion || "",
        horas_estimadas: editing.horas_estimadas ?? "",
        lleva_retirada: !!editing.lleva_retirada,
        fecha_retirada: editing.fecha_retirada ? editing.fecha_retirada.slice(0, 10) : "",
        fecha_programada: editing.fecha_programada ? editing.fecha_programada.slice(0, 10) : "",
        coste_montaje: editing.coste_montaje ?? 0,
        precio_montaje: editing.precio_montaje ?? 0,
        requiere_validacion: !!editing.requiere_validacion,
        referencia_cliente: editing.referencia_cliente || "",
        imagen_url: editing.imagen_url || "",
      });
      setReq(editing.requisitos_montaje ? aFormRequisitos(editing.requisitos_montaje) : null);
      setReqOrigen(editing.requisitos_origen || null);
      setReqAbierto(!!editing.requisitos_montaje); setReqConfirm(null);
    } else if (open) {
      setReq(null); setReqOrigen(null); setReqAbierto(false); setReqConfirm(null);
      setForm({ tipo: "solo_montaje", espacio_id: "", zona_id: "", estado: "pendiente",
        descripcion: "", horas_estimadas: "",
        lleva_retirada: false, fecha_retirada: "",
        fecha_programada: fechaObjetivoDefault || "",  // Iter 111 · prefill con fecha objetivo
        coste_montaje: 0, precio_montaje: 0,
        requiere_validacion: false,
        referencia_cliente: "",
        imagen_url: "" });
    }
  }, [editing, open, fechaObjetivoDefault]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.espacio_id) delete payload.espacio_id;
      if (!payload.zona_id) delete payload.zona_id;
      if (payload.referencia_cliente !== undefined && !String(payload.referencia_cliente).trim()) {
        payload.referencia_cliente = null;
      }
      if (payload.horas_estimadas === "") delete payload.horas_estimadas;
      else payload.horas_estimadas = parseFloat(payload.horas_estimadas);
      payload.coste_montaje = parseFloat(payload.coste_montaje) || 0;
      payload.precio_montaje = parseFloat(payload.precio_montaje) || 0;
      payload.imagen_url = payload.imagen_url || null;
      // Requisitos: snapshot real de la línea (si no hay nada informado y hay zona, el servidor copia el de la zona al crear)
      const reqPayload = aPayloadRequisitos(req);
      if (reqPayload || isEdit) {
        payload.requisitos_montaje = reqPayload;
        payload.requisitos_origen = reqPayload ? { ...(reqOrigen || {}), zona_id: reqOrigen?.zona_id || payload.zona_id || null, modificado: !!reqPersonalizado } : null;
      }
      if (!payload.lleva_retirada) {
        delete payload.fecha_retirada;
      } else if (payload.fecha_retirada) {
        payload.fecha_retirada = new Date(payload.fecha_retirada).toISOString();
      }
      // Fecha de montaje OPCIONAL: vacía → null (nunca se inventa una fecha)
      if (payload.fecha_programada) {
        payload.fecha_programada = new Date(payload.fecha_programada).toISOString();
      } else {
        payload.fecha_programada = null;
      }
      let creada = null;
      if (isEdit) {
        await api.patch(`/lineas/${editing.id}`, payload);
      } else {
        payload.peticion_id = peticionId;
        const { data } = await api.post("/lineas", payload);
        creada = data;
      }
      handleClose();
      onCreated(creada);  // la línea ya está persistida; el paso "Definir producción" es sólo UI
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "linea_bloqueada_por_decision") {
        toast.error(d.msg, { duration: 10000, action: { label: "Resolver no realizada", onClick: () => { window.location.href = "/no-realizadas"; } } });
        return;
      }
      toast.error(d?.msg || (typeof d === "string" ? d : null) || "No se pudo guardar la línea");
    } finally { setSaving(false); }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(v); }}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold" data-testid="new-linea-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva línea
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? "Editar línea de trabajo" : "Nueva línea de trabajo"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Tipos (puedes combinar varios)</Label>
              {/* Presets rápidos — orden por frecuencia (lo más habitual primero) */}
              {(() => {
                const sortedTipos = JSON.stringify([...(form.tipos || [])].sort());
                const isMatch = (arr) => sortedTipos === JSON.stringify([...arr].sort());
                const presetCls = (active) => `px-3 py-1.5 rounded-lg text-xs border transition-colors flex items-center gap-1.5 ${
                  active
                    ? "bg-amber-500/20 border-amber-500/60 text-amber-200"
                    : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-amber-200 hover:border-amber-500/40"
                }`;
                return (
                  <div className="flex flex-wrap gap-2 mt-1.5 mb-2">
                    <button type="button" data-testid="preset-solo-montaje"
                      onClick={() => setForm({ ...form, tipos: ["montaje"], tipo: "montaje" })}
                      className={presetCls(isMatch(["montaje"]))}>
                      📦 Solo Montaje
                    </button>
                    <button type="button" data-testid="preset-prod-montaje"
                      onClick={() => setForm({ ...form, tipos: ["produccion", "montaje"], tipo: "produccion_montaje" })}
                      className={presetCls(isMatch(["produccion", "montaje"]))}>
                      🏭 Producción + Montaje
                    </button>
                    <button type="button" data-testid="preset-completo"
                      onClick={() => setForm({ ...form, tipos: ["diseno", "produccion", "montaje"], tipo: "diseno_produccion_montaje" })}
                      className={presetCls(isMatch(["diseno", "produccion", "montaje"]))}>
                      🎨 Diseño + Producción + Montaje
                    </button>
                    <button type="button" data-testid="preset-solo-retirada"
                      onClick={() => setForm({ ...form, tipos: ["retirada"], tipo: "retirada" })}
                      className={presetCls(isMatch(["retirada"]))}>
                      ♻️ Solo Retirada
                    </button>
                    <button type="button" data-testid="preset-prod-envio"
                      onClick={() => setForm({ ...form, tipos: ["produccion", "envio"], tipo: "produccion_envio" })}
                      className={presetCls(isMatch(["produccion", "envio"]))}>
                      📦 Producción + Envío
                    </button>
                  </div>
                );
              })()}
              <div className="mt-1.5">
                <TiposLineaMulti
                  value={form.tipos}
                  onChange={(v) => setForm({ ...form, tipos: v, tipo: v[0] || "otros" })}
                  dataTestId="form-linea-tipos-multi"
                />
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                Pulsa un atajo para preseleccionar los tipos. Después puedes ajustar.
              </div>
            </div>
            <div>
              <Label>Estado</Label>
              {editing?.bloqueo_decision && (
                <div className="mb-1 text-[11px] text-red-200 bg-red-500/10 border border-red-500/40 rounded p-2" data-testid="linea-bloqueada-aviso">
                  ⏳ Retenida: parada NO realizada en {editing.bloqueo_decision.ruta_codigo || "ruta"} #{editing.bloqueo_decision.parada_orden} ({editing.bloqueo_decision.motivo}). Cambiar el estado aquí no la hace planificable: <a href="/no-realizadas" className="underline font-semibold">resolver no realizada →</a>
                </div>
              )}
              <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {/* Flujo v2 — sólo estados manuales. Si la línea ya tiene un estado automático, se mantiene visible. */}
                  {(ESTADOS_LINEA_MANUALES.includes(form.estado)
                    ? ESTADOS_LINEA_MANUALES
                    : [form.estado, ...ESTADOS_LINEA_MANUALES]
                  ).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label>Espacio (montaje en)</Label>
                <Link to="/espacios" target="_blank" rel="noopener noreferrer"
                  className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Nuevo espacio
                </Link>
              </div>
              <EspacioCombobox
                value={form.espacio_id}
                onChange={(v) => { setForm({ ...form, espacio_id: v, zona_id: "" }); if (!reqPersonalizado) { setReq(null); setReqOrigen(null); } }}
                espacios={espacios}
                clienteId={clienteId}
                placeholder="Buscar espacio: centro, ciudad…"
                testId="form-linea-espacio"
                allowNone
              />
            </div>
            {form.espacio_id && (
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label>Zona del espacio <span className="text-zinc-500 text-xs">(opcional)</span></Label>
                  {espacioSel && (
                    <Link to="/espacios" target="_blank" rel="noopener noreferrer"
                      className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Nueva zona (editando "{espacioSel.nombre}")
                    </Link>
                  )}
                </div>
                {zonasDisponibles.length > 0 ? (
                  <Select value={form.zona_id} onValueChange={onZonaChange}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {zonasDisponibles.map((z) => (
                        <SelectItem key={z.id} value={z.id}>
                          {z.nombre} {z.ancho_cm && z.alto_cm ? `(${z.ancho_cm}×${z.alto_cm}cm)` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-xs text-zinc-500 italic bg-zinc-900/40 rounded px-3 py-2">
                    Este espacio no tiene zonas definidas. Añádelas en la página de Espacios.
                  </div>
                )}
              </div>
            )}
            {(form.zona_id || (req && hayRequisitos(req))) && (
              <div className="col-span-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-2" data-testid="linea-requisitos-bloque">
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => setReqAbierto((v) => !v)} data-testid="linea-requisitos-toggle" className="text-xs font-semibold text-amber-200 inline-flex items-center gap-1">
                    {reqAbierto ? "▾" : "▸"} Requisitos de montaje
                  </button>
                  {req && hayRequisitos(req) ? (
                    <>
                      {req.material_habitual && <span className="text-[11px] text-zinc-300">{req.material_habitual}</span>}
                      <RequisitosChips req={aPayloadRequisitos(req)} testId="linea-req-chips" />
                      <span className="text-[10px] text-zinc-500" data-testid="linea-req-origen">{reqPersonalizado ? "personalizados en esta línea" : `heredados de «${reqOrigen?.zona_nombre || zonaSel?.nombre || "zona"}»`}</span>
                    </>
                  ) : <span className="text-[11px] text-zinc-500 italic" data-testid="linea-req-vacio">{zonaSel?.requisitos_montaje ? "sin requisitos en esta línea" : "la zona no tiene requisitos definidos · puedes añadirlos sólo para este trabajo"}</span>}
                  {zonaSel?.requisitos_montaje && hayRequisitos(zonaSel.requisitos_montaje) && reqPersonalizado && (
                    <button type="button" onClick={() => setReqConfirm({ zona: zonaSel })} data-testid="linea-req-recargar" className="ml-auto text-[10px] text-cyan-300 underline">Recargar de la zona</button>
                  )}
                </div>
                {reqAbierto && (
                  <div className="mt-2">
                    <RequisitosForm value={req || REQ_VACIO} onChange={(v) => setReq(v)} testId="linea-req-form" materialLabel="Material habitual (sugerido)" />
                    <div className="text-[10px] text-zinc-500 mt-1">Copia editable para este trabajo: cambiarla no altera la zona maestra. El material habitual es sólo una sugerencia.</div>
                  </div>
                )}
                {reqConfirm && (
                  <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs" data-testid="linea-req-confirm">
                    <div className="text-amber-100">Esta línea tiene requisitos personalizados. ¿Sustituirlos por los de la zona «{reqConfirm.zona?.nombre || "—"}»?</div>
                    <div className="flex gap-2 mt-1.5">
                      <Button type="button" size="sm" onClick={() => { cargarDeZona(reqConfirm.zona); setReqConfirm(null); }} data-testid="linea-req-confirm-sustituir" className="h-7 text-xs bg-amber-500 hover:bg-amber-400 text-zinc-900">Sustituir</Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setReqConfirm(null)} data-testid="linea-req-confirm-mantener" className="h-7 text-xs bg-transparent border-zinc-700 text-zinc-200">Mantener los míos</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <Label>Horas estimadas</Label>
              <Input type="number" step="0.5" value={form.horas_estimadas}
                onChange={(e) => setForm({ ...form, horas_estimadas: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Fecha de montaje <span className="text-zinc-500 font-normal">(opcional)</span></Label>
              <div className="flex items-center gap-1.5">
                <Input type="date" value={form.fecha_programada}
                  onChange={(e) => setForm({ ...form, fecha_programada: e.target.value })}
                  data-testid="form-linea-fecha-programada"
                  className="bg-zinc-900 border-zinc-800" />
                {form.fecha_programada ? (
                  <button type="button" onClick={() => setForm({ ...form, fecha_programada: "" })} data-testid="form-linea-sin-fecha-btn"
                    className="shrink-0 h-9 px-2 text-[11px] rounded border border-zinc-700 text-zinc-300 hover:border-zinc-500 whitespace-nowrap">Sin fecha por ahora</button>
                ) : fechaObjetivoDefault ? (
                  <button type="button" onClick={() => setForm({ ...form, fecha_programada: fechaObjetivoDefault })} data-testid="form-linea-usar-objetivo-btn"
                    className="shrink-0 h-9 px-2 text-[11px] rounded border border-amber-500/40 text-amber-200 hover:border-amber-400 whitespace-nowrap">Usar objetivo {fechaObjetivoDefault.slice(8, 10)}/{fechaObjetivoDefault.slice(5, 7)}</button>
                ) : null}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1" data-testid="form-linea-fecha-ayuda">
                {form.fecha_programada
                  ? (fechaObjetivoDefault && form.fecha_programada === fechaObjetivoDefault ? "Coincide con la fecha objetivo de la petición · editable" : "Cada línea puede montarse un día distinto — así se planifica bien la ruta")
                  : "Sin fecha por ahora · déjala vacía si se programará más adelante (aparecerá en «Sin fecha» del calendario)"}
                {editing?.fecha_montaje_prevista && <span className="block text-cyan-300/80">Cita con el cliente: {String(editing.fecha_montaje_prevista).slice(0, 10)} (no se modifica aquí)</span>}
              </div>
            </div>
            <div className="col-span-2">
              <ImagenUploadControl value={form.imagen_url || null} nombre={espacioSel?.nombre || ""}
                onChange={(u) => setForm({ ...form, imagen_url: u || "" })}
                folder="dnf/lineas" label="Imagen de la línea (miniatura para montadores y listados)" />
            </div>
            {(form.tipo === "solo_montaje" || form.tipo === "produccion_montaje" ||
              form.tipo === "diseno_produccion_montaje") && (
              <div className="col-span-2 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.lleva_retirada}
                    onChange={(e) => setForm({ ...form, lleva_retirada: e.target.checked })}
                    data-testid="form-linea-lleva-retirada"
                    className="accent-amber-500 w-4 h-4" />
                  <span className="text-sm text-amber-200 font-medium">¿Lleva retirada?</span>
                  <span className="text-xs text-zinc-500">— se generará automáticamente la línea de retirada</span>
                </label>
                {form.lleva_retirada && (
                  <div>
                    <Label className="text-xs">Fecha de retirada *</Label>
                    <Input type="date" required value={form.fecha_retirada}
                      onChange={(e) => setForm({ ...form, fecha_retirada: e.target.value })}
                      data-testid="form-linea-fecha-retirada"
                      className="bg-zinc-900 border-zinc-800" />
                  </div>
                )}
              </div>
            )}
            <div className="col-span-2 bg-cyan-500/5 border border-cyan-500/30 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.requiere_validacion}
                  onChange={(e) => setForm({ ...form, requiere_validacion: e.target.checked })}
                  data-testid="form-linea-requiere-validacion"
                  className="accent-cyan-500 w-4 h-4" />
                <span className="text-sm text-cyan-200 font-medium">🛡 Requiere validación antes de cerrar</span>
              </label>
              <div className="text-[11px] text-zinc-500 mt-1 ml-6">
                El montador no podrá marcar como completada directamente. Deberá subir fotos del montaje
                y un admin dará el OK. Ideal para clientes premium (TAVOLA, Aromas…).
              </div>
            </div>
            <div className="col-span-2">
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                rows={2} className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs flex items-center gap-2">
                Ref. cliente <span className="text-[10px] text-zinc-500 normal-case">(Nº pedido/referencia que pide el cliente; se imprimirá en albarán y factura)</span>
              </Label>
              <Input value={form.referencia_cliente || ""}
                onChange={(e) => setForm({ ...form, referencia_cliente: e.target.value })}
                placeholder="P-2026/00123, OC-456…"
                data-testid="form-linea-referencia-cliente"
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="col-span-2 mt-1 pt-3 border-t border-zinc-800">
              <div className="text-[10px] uppercase tracking-[0.16em] text-amber-400/80 mb-2">Mano de obra · Montaje</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Coste montaje (€)</Label>
                  <Input type="number" step="0.01" value={form.coste_montaje}
                    onChange={(e) => setForm({ ...form, coste_montaje: e.target.value })}
                    data-testid="form-linea-coste-montaje"
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label className="text-xs">Precio montaje (€)</Label>
                  <Input type="number" step="0.01" value={form.precio_montaje}
                    onChange={(e) => setForm({ ...form, precio_montaje: e.target.value })}
                    data-testid="form-linea-precio-montaje"
                    className="bg-zinc-900 border-zinc-800" />
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">Mano de obra/desplazamiento. Se suma al total de la petición además del coste de los materiales.</div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} className="bg-transparent border-zinc-700">Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="form-linea-submit"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              {saving ? "Guardando..." : (isEdit ? "Guardar cambios" : "Crear línea")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
